import type { ValueBase } from "@react-types/shared";
import {
  type FieldPath,
  type FieldValues,
  type RegisterOptions,
  useForm,
  type UseFormProps,
  type UseFormRegister,
  type UseFormRegisterReturn,
  type UseFormReturn,
  useFormState,
} from "react-hook-form";

/**
 * Used to make the onChange callback compatible with react-aria which doesn't send an event but the value of the field
 */
export function useRegisterReactAria<
  TFieldValues extends FieldValues,
  TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  register: UseFormRegister<TFieldValues>,
): (
  name: TFieldName,
  options?: RegisterOptions<TFieldValues, TFieldName>,
) => Omit<UseFormRegisterReturn<TFieldName>, "onChange"> & Pick<ValueBase<unknown>, "onChange"> {
  return (name, options) => {
    const { onChange, ...rest } = register(name, options);

    return {
      ...rest,
      onChange: (value: unknown) => {
        // We need to expose the name too to make the formState working, like trigger "isDirty", ...
        void onChange({ target: { value, name } });
      },
    };
  };
}

/**
 * Used to make the onChange callback compatible with react-aria. @see useRegisterReactAria
 * The rest of the react-hook-form useForm() function is simply exposed.
 *
 * Note: This hook uses useFormState internally to ensure React Compiler compatibility.
 * When React Compiler is enabled, destructuring from formState breaks the Proxy subscription.
 */
export function useFormAria<
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues extends FieldValues | undefined = undefined,
>(
  props?: UseFormProps<TFieldValues, TContext, TTransformedValues>,
): Omit<UseFormReturn<TFieldValues, TContext, TTransformedValues>, "register" | "formState"> & {
  register: ReturnType<typeof useRegisterReactAria<TFieldValues>>;
  formState: ReturnType<typeof useFormState<TFieldValues>>;
} {
  const { register: _register, control, ...rest } = useForm<TFieldValues, TContext, TTransformedValues>(props);
  const register = useRegisterReactAria(_register);
  const formState = useFormState({ control });
  return { ...rest, control, register, formState };
}

export type FormAriaHook<
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues extends FieldValues | undefined = undefined,
> = ReturnType<typeof useFormAria<TFieldValues, TContext, TTransformedValues>>;
